import { useCallback } from 'react';
import { persist } from 'zustand/middleware';
import { shallow } from 'zustand/shallow';
import { createWithEqualityFn } from 'zustand/traditional';

// Planners the user never returns to shouldn't accumulate in localStorage
// forever. Object string keys iterate in insertion order, so re-inserting on
// every change makes the leading entries the least recently chosen.
const MAX_REMEMBERED_PLANNERS = 50;

type State = {
  viewByExperienceId: Record<string, string>;
  rememberView: (experienceId: string, childId: string) => void;
};

export const useLastPlannerViewStore = createWithEqualityFn<State>()(
  persist(
    (set, get) => ({
      viewByExperienceId: {},
      rememberView: (experienceId: string, childId: string) => {
        const current = get().viewByExperienceId;
        const keys = Object.keys(current);
        // Re-insert even when the view itself is unchanged, so merely reopening
        // a planner refreshes its place in the eviction queue — otherwise a
        // planner visited daily but never re-picked would age out behind ones
        // touched once. Bail once it's already the most recent: useActiveView
        // records from an effect that runs in each of its many simultaneous
        // mounts (and twice per mount under StrictMode), so only the first call
        // should do work.
        if (
          current[experienceId] === childId &&
          keys[keys.length - 1] === experienceId
        ) {
          return;
        }

        const entries = Object.entries(current).filter(
          ([id]) => id !== experienceId
        );
        entries.push([experienceId, childId]);

        set({
          viewByExperienceId: Object.fromEntries(
            entries.slice(-MAX_REMEMBERED_PLANNERS)
          ),
        });
      },
    }),
    { name: '_ah_planner_last_view' }
  ),
  shallow
);

/**
 * The view (child experience) the user last landed on in a given planner,
 * persisted across sessions so returning to the planner reopens it.
 *
 * The stored id is not validated here — the caller already matches it against
 * the planner's current children, so a removed view degrades to the default
 * rather than rendering nothing.
 */
export const useLastPlannerView = (experienceId: string) => {
  const lastViewId = useLastPlannerViewStore(
    (state) => state.viewByExperienceId[experienceId]
  );
  const setLastView = useLastPlannerViewStore((state) => state.rememberView);

  const rememberView = useCallback(
    (childId: string) => setLastView(experienceId, childId),
    [setLastView, experienceId]
  );

  return { lastViewId, rememberView };
};
